/**
 * Player focus: study a few players without losing the game around them
 * (founder ask, 2026-08-27).
 *
 * Selecting players narrows every movement layer — trails, speed trails,
 * heat map — to just those, while everyone else keeps being drawn as a small
 * team-coloured dot and the ball is untouched. The point is that a run only
 * means something in context: dropping the other twenty players entirely
 * would answer "where did he go" while destroying "why".
 *
 * Kept separate from `highlightedPlayers`, which the moments list drives to
 * put a gold ring on someone. Overloading that would mean selecting a moment
 * silently dimmed the whole pitch.
 */

/** "team_jersey", e.g. "0_16" — the key the whole page already uses. */
export type PlayerKey = string

export const playerKey = (team: number, jersey: number): PlayerKey => `${team}_${jersey}`

/** Toggle membership, returning a new set (state stays immutable). */
export function toggleFocus(focus: PlayerKey[], key: PlayerKey): PlayerKey[] {
  return focus.includes(key) ? focus.filter(k => k !== key) : [...focus, key]
}

/**
 * Which player keys a movement layer should draw.
 *
 * Focus wins when set; otherwise fall back to the moment highlight, which is
 * what the trails did before focus existed. Empty means "everyone", so an
 * untouched page behaves exactly as it did.
 */
export function layerSubject(focus: PlayerKey[], highlighted: string[]): string[] {
  if (focus.length) return focus
  return highlighted
}

export interface HitTarget {
  key: PlayerKey
  team: number
  jersey: number
  /** Canvas pixels. */
  x: number
  y: number
}

/**
 * The player under a click, or null.
 *
 * Radius is generous (the drawn circle is 8px, the target 16) because these
 * are small marks on a wide pitch and a miss silently does nothing. Nearest
 * wins, so overlapping players resolve to the one actually clicked.
 */
export function hitTestPlayer(
  targets: HitTarget[],
  x: number,
  y: number,
  radius = 16,
): HitTarget | null {
  let best: HitTarget | null = null
  let bestDist = radius * radius
  for (const t of targets) {
    const dx = t.x - x
    const dy = t.y - y
    const d2 = dx * dx + dy * dy
    if (d2 <= bestDist) {
      best = t
      bestDist = d2
    }
  }
  return best
}
